import { TextLink } from '@edsn/pie-components';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import type { Option } from '#pie/components/input-radio/InputRadio';
import type { CreateDataRequestDto } from '#pie/internal/api/pie-bff';
import { DataLevel, DataLevelZipCode } from '#pie/internal/api/pie-bff';
import { typedFormFields } from '#pie/utils/typedFormFields';

const { ConditionalField, FormRadio, FormText } =
  typedFormFields<CreateDataRequestDto>();

export const Level = () => {
  // Voor welk(e) gebied(en) of adres(sen) wil je data ontvangen?
  const { t } = useTranslation('dataRequest');
  const { t: thl } = useTranslation('hyperlinks');
  const { watch } = useFormContext<CreateDataRequestDto>();

  const options: Option[] = [
    {
      label: t(`level.${DataLevel.National}`),
      value: DataLevel.National,
    },
    {
      label: t(`level.${DataLevel.ZipCodeRegionalGridOperator}`, {
        netOperator: watch('dataRequestTo')
          ? watch('dataRequestTo')!
              .map((drr) => t(`data_request_receiver.${drr}`))
              .join(', ')
          : t('data_request_receiver.AllGridOperators'),
      }),
      value: DataLevel.ZipCodeRegionalGridOperator,
    },
    {
      label: t(`level.${DataLevel.Province}`),
      value: DataLevel.Province,
    },
    {
      label: t(`level.${DataLevel.ResRegion}`),
      tooltip: (
        <>
          {t('link.see')}
          <TextLink
            href={thl('res_regio.regions')}
            target="_blank noreferror noopener"
          >
            {t('link.here')}
          </TextLink>{' '}
          {t('level.ResRegion.tooltip.part_two')}
        </>
      ),

      value: DataLevel.ResRegion,
    },
    {
      label: t(`level.${DataLevel.Municipality}`),
      value: DataLevel.Municipality,
    },
    {
      label: t(`level.${DataLevel.City}`),
      value: DataLevel.City,
    },
    {
      label: t(`level.${DataLevel.ZipCode}`),
      value: DataLevel.ZipCode,
    },
    {
      label: t(`level.${DataLevel.Address}`),
      value: DataLevel.Address,
    },
    {
      label: t('level.Other'),
      renderInput: (
        <ConditionalField
          field="levelNote"
          condition={(fieldValue) => fieldValue('level') === DataLevel.Other}
          keepDataCondition={(fieldValue) =>
            fieldValue('level') === DataLevel.Province ||
            fieldValue('level') === DataLevel.ResRegion ||
            fieldValue('level') === DataLevel.Municipality ||
            fieldValue('level') === DataLevel.City ||
            fieldValue('level') === DataLevel.Other ||
            fieldValue('level') === DataLevelZipCode.Other
          }
        >
          <FormText
            aria-label={t('other_note')}
            fullWidth
            name="levelNote"
            showWordCount
            rules={{ maxLength: 25, minLength: 2, required: true }}
          />
        </ConditionalField>
      ),
      value: DataLevel.Other,
    },
  ];

  return (
    <ConditionalField
      field="level"
      condition={(formValue) =>
        !!formValue('dataTypeNote') && formValue('dataTypeNote')!.length > 1
      }
    >
      <FormRadio
        label={t('level')}
        name="level"
        options={options}
        infoTooltip={t('level.tooltip')}
        rules={{ required: true }}
      />
    </ConditionalField>
  );
};
